import { useEffect, useRef, useState } from 'react';

const DURATION = 480;

function easeOutCubic(p: number): number {
  return 1 - Math.pow(1 - p, 3);
}

interface CountUpOptions {
  target: number;
  suffix?: string;
  decimals?: number;
  isTime?: boolean;
  enabled?: boolean;
}

export function useCountUp({
  target,
  suffix = '',
  decimals = 0,
  isTime = false,
  enabled = true,
}: CountUpOptions): string {
  const [display, setDisplay] = useState(isTime ? '0m' : `0${suffix}`);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION, 1);
      const eased = easeOutCubic(progress);

      if (isTime) {
        const totalMinutes = Math.round(target * eased);
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        setDisplay(hours > 0 ? `${hours}h ${mins}m` : `${mins}m`);
      } else {
        const current = target * eased;
        const formatted = decimals > 0 ? current.toFixed(decimals) : String(Math.round(current));
        setDisplay(`${formatted}${suffix}`);
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [target, suffix, decimals, isTime, enabled]);

  return display;
}
